/**
 * Route Preview Page - Routific-inspired design
 * Full map with bottom timeline panel showing driver schedules
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Button } from '../components/ui';
import {
  ArrowLeft,
  Package,
  AlertTriangle,
  Loader2,
  Send,
  X,
} from 'lucide-react';
import { previewRoutes } from '../services/routes.api';
import { useCreateRoutes } from '../hooks/useApi';
import type { RoutePreviewResult, PreviewRoute } from '../types';
import { setMapLanguageToArabic, ARABIC_MAP_STYLE } from '../utils/mapbox';
import { isValidCoordinate } from '../utils/coordinates';
import { getBaghdadToday } from '../utils/date';

// Baghdad Warehouse location (must match backend coordinates)
const BAGHDAD_WAREHOUSE = {
  name: 'Baghdad Sorting Center',
  lat: 33.284412,
  lng: 44.448563,
};

// Route colors for different drivers
const ROUTE_COLORS = [
  '#8B5CF6', // Purple
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#F97316', // Orange
  '#6366F1', // Indigo
];

interface LocationState {
  taskUids: string[];
  routeDate?: string;
  startTime?: string;
  endTime?: string;
  secondsPerParcel?: number;
  baseServiceTimeMin?: number;
  driverMode?: 'auto' | 'manual';
  manualDriverCount?: number;
  optimizationGoal?: 'balanced' | 'minimize_time' | 'minimize_vehicles';
  speedFactor?: number;
  useMerchantTimeWindows?: boolean;
  hubStart?: boolean;
  vehicleCapacity?: number;
}

export default function RoutePreview() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  // Mutation hook for creating routes
  const createRoutesMutation = useCreateRoutes();

  // Map refs
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Ref to prevent double API calls in StrictMode
  const hasInitiatedPreview = useRef(false);

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<RoutePreviewResult | null>(null);
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState<number | null>(null);
  const [selectedStop, setSelectedStop] = useState<{ routeIndex: number; stopIndex: number } | null>(null);
  const [visibleRoutes, setVisibleRoutes] = useState<Set<number>>(new Set());
  const [mapBounds, setMapBounds] = useState<string | null>(null);
  const [mapZoom, setMapZoom] = useState<number>(11);

  // Get task UIDs from navigation state
  const taskUids = useMemo(() => {
    if (state?.taskUids) return state.taskUids;
    const stored = sessionStorage.getItem('selectedTasksForRouting');
    if (stored) {
      try {
        return JSON.parse(stored) as string[];
      } catch {
        return [];
      }
    }
    return [];
  }, [state]);

  const routeDate = state?.routeDate || getBaghdadToday();

  // Get configuration - MUST use the times configured by user in RouteConfig
  // Priority: navigation state > sessionStorage > defaults
  const routeConfig = useMemo(() => {
    // Priority 1: Navigation state (freshest, passed directly from RouteConfig)
    if (state?.startTime && state?.endTime) {
      return {
        startTime: state.startTime as string,
        endTime: state.endTime as string,
        secondsPerParcel: state.secondsPerParcel ?? 5,
        baseServiceTimeMin: state.baseServiceTimeMin ?? 5,
        driverMode: (state.driverMode || 'auto') as 'auto' | 'manual',
        manualDriverCount: state.manualDriverCount,
        optimizationGoal: (state.optimizationGoal || 'balanced') as 'balanced' | 'minimize_time' | 'minimize_vehicles',
        speedFactor: state.speedFactor ?? 1.0,
        useMerchantTimeWindows: state.useMerchantTimeWindows ?? true,
        hubStart: state.hubStart ?? true,
        vehicleCapacity: state.vehicleCapacity ?? 300,
      };
    }

    // Priority 2: sessionStorage (persists across page refreshes)
    const stored = sessionStorage.getItem('routeConfig');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.startTime && parsed.endTime) {
          return {
            startTime: parsed.startTime as string,
            endTime: parsed.endTime as string,
            secondsPerParcel: parsed.secondsPerParcel ?? 5,
            baseServiceTimeMin: parsed.baseServiceTimeMin ?? 5,
            driverMode: (parsed.driverMode || 'auto') as 'auto' | 'manual',
            manualDriverCount: parsed.manualDriverCount,
            optimizationGoal: (parsed.optimizationGoal || 'balanced') as 'balanced' | 'minimize_time' | 'minimize_vehicles',
            speedFactor: parsed.speedFactor ?? 1.0,
            useMerchantTimeWindows: parsed.useMerchantTimeWindows ?? true,
            hubStart: parsed.hubStart ?? true,
            vehicleCapacity: parsed.vehicleCapacity ?? 300,
          };
        }
      } catch {
        // Fall through to defaults
      }
    }

    // Default values (fallback)
    return {
      startTime: '09:00',
      endTime: '18:00',
      secondsPerParcel: 5,
      baseServiceTimeMin: 5,
      driverMode: 'auto' as const,
      optimizationGoal: 'balanced' as const,
      speedFactor: 1.0,
      useMerchantTimeWindows: true,
      hubStart: true,
      vehicleCapacity: 300,
    };
  }, [state]);

  // Initialize map with delay to ensure container has dimensions
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // Small delay to ensure container has rendered with dimensions
    const timer = setTimeout(() => {
      if (!mapContainer.current) return;

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: ARABIC_MAP_STYLE,
        center: [BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat],
        zoom: 11,
      });

      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

      // Set Arabic language for map labels
      setMapLanguageToArabic(map.current);

      map.current.on('load', () => {
        setMapLoaded(true);
        // Trigger resize to ensure map fills container
        map.current?.resize();
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      map.current?.remove();
      map.current = null;
    };
  }, []);

  // Auto-generate preview on mount (with StrictMode protection)
  useEffect(() => {
    if (taskUids.length > 0 && !preview && !autoGenerating && !hasInitiatedPreview.current) {
      hasInitiatedPreview.current = true;
      handleAutoGeneratePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskUids]);

  useEffect(() => {
    if (preview?.routes) {
      setVisibleRoutes(new Set(preview.routes.map((_, i) => i)));
    }
  }, [preview]);


  // Draw routes on map when preview or visibility changes
  useEffect(() => {
    if (!map.current || !mapLoaded || !preview) return;

    // Clear existing layers and markers
    clearMapLayers();

    // Add warehouse marker
    addWarehouseMarker();

    // Draw routes with straight lines
    preview.routes.forEach((route, routeIndex) => {
      if (visibleRoutes.has(routeIndex)) {
        drawRoute(route, routeIndex);
      }
    });

    // Add unassigned task markers (orange markers with !)
    addUnassignedTaskMarkers();

    // Fit bounds to show all stops (only on initial load, not on selection changes)
    fitMapBounds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, mapLoaded, visibleRoutes]);

  // Redraw routes when selection changes (without fitting bounds)
  useEffect(() => {
    if (!map.current || !mapLoaded || !preview || selectedRouteIndex === null) return;

    // Clear and redraw to update marker styles for selection
    clearMapLayers();
    addWarehouseMarker();
    preview.routes.forEach((route, routeIndex) => {
      if (visibleRoutes.has(routeIndex)) {
        drawRoute(route, routeIndex);
      }
    });
    addUnassignedTaskMarkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRouteIndex]);

  const clearMapLayers = () => {
    if (!map.current) return;

    // Remove markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Remove route layers and sources
    const style = map.current.getStyle();
    if (style?.layers) {
      style.layers.forEach(layer => {
        if (layer.id.startsWith('route-')) {
          map.current?.removeLayer(layer.id);
        }
      });
    }
    if (style?.sources) {
      Object.keys(style.sources).forEach(sourceId => {
        if (sourceId.startsWith('route-')) {
          map.current?.removeSource(sourceId);
        }
      });
    }
  };

  const addWarehouseMarker = () => {
    if (!map.current) return;

    const el = document.createElement('div');
    el.innerHTML = `
      <div style="
        width: 40px;
        height: 40px;
        background: #7C3AED;
        border: 3px solid white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        cursor: pointer;
      ">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
          <path d="M4 21V9L12 3L20 9V21H14V14H10V21H4Z"/>
        </svg>
      </div>
    `;

    const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
      <div style="padding: 8px;">
        <strong>Baghdad Sorting Center</strong><br/>
        <span style="color: #666; font-size: 12px;">Start/End Location</span>
      </div>
    `);

    const marker = new mapboxgl.Marker({ element: el })
      .setLngLat([BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat])
      .setPopup(popup)
      .addTo(map.current);

    markersRef.current.push(marker);
  };

  const addUnassignedTaskMarkers = () => {
    if (!map.current || !preview?.unassignedTasks) return;

    preview.unassignedTasks.forEach((task) => {
      if (!isValidCoordinate(task.lat, task.lng)) return;
      const hasTimeWindow = task.hasTimeWindow;
      const timeWindowText = task.timeWindow
        ? `${task.timeWindow.start} - ${task.timeWindow.end}`
        : '';
      const orderCount = task.numberOfOrders ?? 0;

      const el = document.createElement('div');
      el.innerHTML = `
        <div style="
          min-width: 24px;
          height: 24px;
          padding: 0 6px;
          background: #F97316;
          border: 2px solid ${hasTimeWindow ? '#8B5CF6' : 'white'};
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 11px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          cursor: pointer;
        ">${orderCount}</div>
      `;

      const popup = new mapboxgl.Popup({ offset: 25, closeButton: false }).setHTML(`
        <div style="padding: 12px; min-width: 200px;">
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
            <div style="
              width: 24px;
              height: 24px;
              background: #F97316;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: bold;
              font-size: 14px;
            ">!</div>
            <strong style="color: #F97316;">Unassigned</strong>
          </div>
          <div style="font-size: 13px; color: #333; margin-bottom: 4px;">
            ${task.merchantName || task.platformCode}
          </div>
          <div style="font-size: 12px; color: #666; margin-bottom: 8px;">
            ${task.areaName || 'Unknown Area'}
          </div>
          <div style="display: flex; gap: 16px; font-size: 12px; color: #666;">
            <span>📦 ${task.numberOfOrders} orders</span>
          </div>
          ${hasTimeWindow ? `
            <div style="margin-top: 8px; padding: 6px 8px; background: #F3E8FF; border-radius: 4px; font-size: 12px; color: #7C3AED;">
              <strong>⏰ Preferred Time:</strong> ${timeWindowText}
            </div>
          ` : ''}
          <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #eee; font-size: 11px; color: #999;">
            Could not fit in time window
          </div>
        </div>
      `);

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([task.lng, task.lat])
        .setPopup(popup)
        .addTo(map.current!);

      markersRef.current.push(marker);
    });
  };

  const drawRoute = (route: PreviewRoute, routeIndex: number) => {
    if (!map.current || route.stops.length === 0) return;

    const color = ROUTE_COLORS[routeIndex % ROUTE_COLORS.length];
    const isSelected = selectedRouteIndex === routeIndex;

    // Build coordinates based on hub_start setting (filter invalid coordinates)
    // If hubStart: warehouse -> stops -> warehouse
    // If not hubStart: stops -> warehouse (drivers start from their zone)
    const stopCoordinates = route.stops
      .filter(stop => isValidCoordinate(stop.lat, stop.lng))
      .map(stop => [stop.lng, stop.lat] as [number, number]);
    const coordinates: [number, number][] = routeConfig.hubStart
      ? [
        [BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat], // Start from warehouse
        ...stopCoordinates,
        [BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat], // Return to warehouse
      ]
      : [
        ...stopCoordinates,
        [BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat], // Only return to warehouse
      ];

    // Add route line
    const sourceId = `route-line-${routeIndex}`;
    const layerId = `route-layer-${routeIndex}`;

    if (!map.current.getSource(sourceId)) {
      map.current.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates,
          },
        },
      });

      map.current.addLayer({
        id: layerId,
        type: 'line',
        source: sourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': color,
          'line-width': isSelected ? 4.5 : 3,
          'line-opacity': isSelected ? 1 : 0.7,
        },
      });
    }

    // Add stop markers - group by location to handle overlapping markers
    const locationGroups = new Map<string, { stop: typeof route.stops[0], stopIndex: number }[]>();
    route.stops.forEach((stop, stopIndex) => {
      const key = `${stop.lat.toFixed(5)},${stop.lng.toFixed(5)}`;
      if (!locationGroups.has(key)) {
        locationGroups.set(key, []);
      }
      locationGroups.get(key)!.push({ stop, stopIndex });
    });

    route.stops.forEach((stop, stopIndex) => {
      if (!isValidCoordinate(stop.lat, stop.lng)) {
        return;
      }

      const isStopSelected = selectedStop?.routeIndex === routeIndex && selectedStop?.stopIndex === stopIndex;
      const hasTimeWindow = stop.hasTimeWindow;
      const isRTO = stop.taskType === 'rto';
      const isCash = stop.taskType === 'cash';
      const timeWindowText = stop.timeWindow
        ? `${stop.timeWindow.start} - ${stop.timeWindow.end}`
        : '';

      // Check if this stop overlaps with others at same location
      const locationKey = `${stop.lat.toFixed(5)},${stop.lng.toFixed(5)}`;
      const overlappingStops = locationGroups.get(locationKey) || [];
      const overlapIndex = overlappingStops.findIndex(s => s.stopIndex === stopIndex);
      const hasOverlap = overlappingStops.length > 1;

      // Offset overlapping markers - larger offset so they're visible when zoomed out
      // 0.0005 degrees ≈ 50 meters, visible at zoom level 14+
      const offsetAngle = hasOverlap ? (overlapIndex * (360 / overlappingStops.length)) * (Math.PI / 180) : 0;
      const offsetDistance = hasOverlap ? 0.0005 : 0; // ~50 meters offset
      const offsetLng = stop.lng + Math.cos(offsetAngle) * offsetDistance;
      const offsetLat = stop.lat + Math.sin(offsetAngle) * offsetDistance;

      // All markers use route color with sequence number, RTO has yellow indicator badge
      const el = document.createElement('div');
      el.innerHTML = `
        <div style="position: relative;">
          <div style="
            width: ${isStopSelected ? 32 : 26}px;
            height: ${isStopSelected ? 32 : 26}px;
            background: ${color};
            border: 2px solid ${hasTimeWindow ? '#8B5CF6' : 'white'};
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: bold;
            font-size: ${isStopSelected ? 14 : 12}px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            cursor: pointer;
            transition: transform 0.2s ease;
          " 
          onmouseover="this.style.transform='scale(1.3)'; this.style.zIndex='1000';"
          onmouseout="this.style.transform='scale(1)'; this.style.zIndex='auto';"
          >${stopIndex + 1}</div>
          ${isRTO ? `
            <div style="
              position: absolute;
              top: -4px;
              right: -4px;
              width: 14px;
              height: 14px;
              background: #F59E0B;
              border: 1.5px solid white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 1px 3px rgba(0,0,0,0.3);
            ">
              <svg width="8" height="8" viewBox="0 0 24 24" fill="white">
                <path d="M19 7v4H5.83l3.58-3.59L8 6l-6 6 6 6 1.41-1.41L5.83 13H21V7h-2z"/>
              </svg>
            </div>
          ` : isCash ? `
            <div style="
              position: absolute;
              top: -4px;
              right: -4px;
              width: 14px;
              height: 14px;
              background: #D97706;
              border: 1.5px solid white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 1px 3px rgba(0,0,0,0.3);
              font-size: 8px;
              color: white;
              font-weight: bold;
            ">$</div>
          ` : ''}
          ${hasTimeWindow ? `
            <div style="
              position: absolute;
              top: -4px;
              left: -4px;
              width: 14px;
              height: 14px;
              background: #3B82F6;
              border: 1.5px solid white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 1px 3px rgba(0,0,0,0.3);
            ">
              <svg width="8" height="8" viewBox="0 0 24 24" fill="white">
                <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.2 3.2.8-1.3-4.5-2.7V7z"/>
              </svg>
            </div>
          ` : ''}
        </div>
      `;

      el.addEventListener('click', () => {
        setSelectedRouteIndex(routeIndex);
        setSelectedStop({ routeIndex, stopIndex });
      });

      const taskTypeBadge = isRTO
        ? '<span style="background: #F59E0B; color: white; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 600;">RTO</span>'
        : isCash
        ? '<span style="background: #D97706; color: white; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 600;">Cash</span>'
        : '<span style="background: #3B82F6; color: white; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 600;">Pickup</span>';

      const popup = new mapboxgl.Popup({ offset: 25, closeButton: false }).setHTML(`
        <div style="padding: 12px; min-width: 200px;">
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
            <div style="
              width: 24px;
              height: 24px;
              background: ${color};
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: bold;
              font-size: 12px;
            ">${stopIndex + 1}</div>
            <strong>${stop.merchantName || 'Other'}</strong>
            ${taskTypeBadge}
          </div>
          <div style="font-size: 12px; color: #666; margin-bottom: 8px;">
            ${stop.areaName || 'Not Applicable'}
          </div>
          <div style="display: flex; gap: 16px; font-size: 12px; color: #666;">
            <span>📦 ${stop.numberOfOrders} orders</span>
            <span>🕐 ${new Date(stop.arrivalTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Baghdad' })}</span>
          </div>
          ${hasTimeWindow ? `
            <div style="margin-top: 8px; padding: 6px 8px; background: #F3E8FF; border-radius: 4px; font-size: 12px; color: #7C3AED;">
              <strong>⏰ Preferred Time:</strong> ${timeWindowText}
            </div>
          ` : ''}
        </div>
      `);

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([offsetLng, offsetLat])
        .setPopup(popup)
        .addTo(map.current!);

      markersRef.current.push(marker);
    });
  };

  const fitMapBounds = () => {
    if (!map.current || !preview) return;

    // Check if map canvas has proper dimensions
    const canvas = map.current.getCanvas();
    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      // Retry after a short delay when canvas is ready
      setTimeout(fitMapBounds, 100);
      return;
    }

    const allCoords: [number, number][] = [];

    // Add warehouse if valid
    if (BAGHDAD_WAREHOUSE.lng && BAGHDAD_WAREHOUSE.lat &&
      !isNaN(BAGHDAD_WAREHOUSE.lng) && !isNaN(BAGHDAD_WAREHOUSE.lat)) {
      allCoords.push([BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat]);
    }

    // Add stop coordinates if valid
    preview.routes.forEach((route, routeIndex) => {
      if (visibleRoutes.has(routeIndex)) {
        route.stops.forEach(stop => {
          if (stop.lng && stop.lat && isValidCoordinate(stop.lat, stop.lng)) {
            allCoords.push([stop.lng, stop.lat]);
          }
        });
      }
    });

    // Only fit bounds if we have at least 2 valid coordinates
    if (allCoords.length >= 2) {
      try {
        const bounds = allCoords.reduce(
          (bounds, coord) => bounds.extend(coord),
          new mapboxgl.LngLatBounds(allCoords[0], allCoords[0])
        );

        map.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: 14,
        });
      } catch (err) {
        // Silently ignore - map will show default view
        console.warn('Could not fit map bounds:', err);
      }
    } else if (allCoords.length === 1) {
      // Just center on the single point
      map.current.setCenter(allCoords[0]);
      map.current.setZoom(12);
    }
  };

  const handleAutoGeneratePreview = async () => {
    if (taskUids.length === 0) {
      setError('No tasks selected');
      return;
    }

    setAutoGenerating(true);
    setIsLoading(true);
    setError(null);

    try {
      const result = await previewRoutes({
        taskUids,
        routeDate,
        startLocationMode: routeConfig.hubStart ? 'hub_start' : 'free_start',
        startTime: routeConfig.startTime,
        endTime: routeConfig.endTime,
        secondsPerParcel: routeConfig.secondsPerParcel,
        baseServiceTimeMin: routeConfig.baseServiceTimeMin,
        maxRoutes: routeConfig.driverMode === 'manual' ? routeConfig.manualDriverCount : undefined,
        optimizationGoal: routeConfig.optimizationGoal,
        speedFactor: routeConfig.speedFactor,
        useMerchantTimeWindows: routeConfig.useMerchantTimeWindows,
        vehicleCapacity: routeConfig.vehicleCapacity,
        // The dispatcher hand-picked these task UIDs, so route whatever they selected —
        // pickup, RTO, and cash together. The server's default (pickup_only) is for
        // callers that don't select explicitly.
        routingMode: 'all',
      });

      // Calculate merchants with time windows count
      const merchantsWithTimeWindows = result.routes.reduce((count: number, route) => {
        return count + route.stops.filter((stop) => stop.hasTimeWindow).length;
      }, 0) + (result.unassignedTasks?.filter((task) => task.hasTimeWindow).length || 0);

      // Add to summary
      const enrichedResult = {
        ...result,
        summary: {
          ...result.summary,
          merchantsWithTimeWindows,
        },
      };

      setPreview(enrichedResult);
      
      setTimeout(() => {
        if (map.current && typeof map.current.getBounds === 'function') {
          const bounds = map.current.getBounds();
          setMapBounds(bounds ? JSON.stringify(bounds.toArray()) : null);
          setMapZoom(map.current.getZoom());
        }
      }, 500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate preview');
    } finally {
      setIsLoading(false);
      setAutoGenerating(false);
    }
  };

  const handleConfirmRoutes = async () => {
    if (!preview || preview.routes.length === 0) {
      setError('No routes to create');
      return;
    }

    try {
      const result = await createRoutesMutation.mutateAsync({
        routes: preview.routes.map(route => ({
          driverUid: route.driverUid,
          driverName: route.driverName,
          stops: route.stops.map(stop => ({
            taskUid: stop.taskUid,
            taskType: stop.taskType, // Include task type (pickup/rto)
            lat: stop.lat,
            lng: stop.lng,
            platformCode: stop.platformCode,
            merchantName: stop.merchantName || undefined,
            areaName: stop.areaName || undefined,
            numberOfOrders: stop.numberOfOrders,
            arrivalTime: stop.arrivalTime,
            serviceTimeMin: stop.serviceTimeMin,
          })),
          totalDistanceKm: route.totalDistanceKm,
          totalDurationMin: route.totalDurationMin,
          totalOrders: route.totalOrders,
          totalCost: route.totalCost,
          polyline: route.polyline,
        })),
        routeDate,
        cityId: 1, // Baghdad
        slot: 'pickup',
        mapBounds,
        mapZoom,
      });

      // Navigate to routes page to assign drivers
      navigate('/routes', {
        state: {
          routeRunId: result.routeRunId,
          routeDate,
          message: `Created ${result.totalRoutes} routes. Assign drivers on this page.`,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create routes');
    }
  };

  const handleBack = () => {
    // Go back to configuration page with the same state
    navigate('/routes/configure', {
      state: {
        taskUids,
        routeDate,
      },
    });
  };


  // No tasks selected
  if (taskUids.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-md">
          <AlertTriangle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Tasks Selected</h2>
          <p className="text-gray-600 mb-6">
            Please go back to the Tasks page and select tasks for routing.
          </p>
          <Button onClick={handleBack} leftIcon={<ArrowLeft className="h-4 w-4" />}>
            Back to Tasks
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Enhanced Top Bar with Visual Summary Cards */}
      <div className="bg-white border-b border-gray-200 shadow-sm z-10">
        {/* Top row - Navigation and Actions */}
        <div className="px-6 py-3 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Route Preview</h1>
              <p className="text-sm text-gray-500">{routeDate}</p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-3">
            {preview && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleConfirmRoutes}
                disabled={createRoutesMutation.isPending}
                leftIcon={createRoutesMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              >
                {createRoutesMutation.isPending ? 'Creating...' : 'Create Routes'}
              </Button>
            )}
          </div>
        </div>

        {/* Summary Cards Row */}
        {preview && (() => {
          const totalOrders = preview.routes.reduce((sum, route) => sum + route.totalOrders, 0);
          return (
            <div className="px-6 py-2">
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                {/* Routes Card */}
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-2.5 border border-purple-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="text-lg">🚗</div>
                    <div className="text-[10px] font-medium text-purple-700 uppercase tracking-wide">Routes</div>
                  </div>
                  <div className="text-xl font-bold text-purple-900">{preview.summary.totalRoutes}</div>
                </div>

                {/* Orders Card */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-2.5 border border-green-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="text-lg">📦</div>
                    <div className="text-[10px] font-medium text-green-700 uppercase tracking-wide">Orders</div>
                  </div>
                  <div className="text-xl font-bold text-green-900">{totalOrders}</div>
                </div>

                {/* Distance Card */}
                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-2.5 border border-indigo-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="text-lg">🧭</div>
                    <div className="text-[10px] font-medium text-indigo-700 uppercase tracking-wide">Distance</div>
                  </div>
                  <div className="text-xl font-bold text-indigo-900">{preview.summary.totalDistanceKm.toFixed(1)} km</div>
                </div>

                {/* Duration Card */}
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-2.5 border border-amber-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="text-lg">⏱️</div>
                    <div className="text-[10px] font-medium text-amber-700 uppercase tracking-wide">Duration</div>
                  </div>
                  <div className="text-xl font-bold text-amber-900">{(preview.summary.totalDurationMin / 60).toFixed(1)}h</div>
                </div>

                {/* Cost per Order Card */}
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-2.5 border border-emerald-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="text-lg">💰</div>
                    <div className="text-[10px] font-medium text-emerald-700 uppercase tracking-wide">Cost/Order</div>
                  </div>
                  <div className="text-xl font-bold text-emerald-900">${totalOrders > 0 ? ((preview.summary.totalCost || 0) / totalOrders).toFixed(2) : '0.00'}</div>
                </div>

                {/* Unassigned Card (conditional) */}
                {preview.summary.unassignedTasks > 0 ? (
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-2.5 border border-orange-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="text-lg">⚠️</div>
                      <div className="text-[10px] font-medium text-orange-700 uppercase tracking-wide">Unassigned</div>
                    </div>
                    <div className="text-xl font-bold text-orange-900">{preview.summary.unassignedTasks}</div>
                  </div>
                ) : (
                  <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-2.5 border border-teal-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="text-lg">✅</div>
                      <div className="text-[10px] font-medium text-teal-700 uppercase tracking-wide">Complete</div>
                    </div>
                    <div className="text-xl font-bold text-teal-900">100%</div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Main Content - Map and Timeline */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 relative" style={{ minHeight: '300px' }}>
          {/* Map container */}
          <div 
            ref={mapContainer} 
            data-testid="map-container"
            data-bounds={mapBounds || ''}
            data-zoom={mapZoom.toString()}
            style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} 
          >
            {preview?.routes.map((route, index) => (
              route.polyline && (
                <div
                  key={`polyline-${index}`}
                  data-testid={`route-polyline-${index}`}
                  data-polyline={route.polyline}
                  style={{ display: 'none' }}
                />
              )
            ))}
          </div>

          {/* Loading overlay */}
          {isLoading && (
            <div className="absolute inset-0 bg-white/80 z-20 flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
                <p className="text-gray-600 font-medium">Generating routes...</p>
                <p className="text-gray-500 text-sm">Optimizing with Verso API</p>
              </div>
            </div>
          )}

          {/* Error overlay */}
          {error && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center gap-3 shadow-lg">
              <X className="h-5 w-5 text-red-500" />
              <span className="text-red-700">{error}</span>
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Map controls - top left */}
          <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
            <button className="bg-white p-2 rounded-lg shadow-lg border border-gray-200 hover:bg-gray-50">
              <Package className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>

      </div>

    </div>
  );
}
